/* eslint-disable prettier/prettier */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Row, Select, Checkbox, Tabs, message } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import _keys from "lodash/keys";
import paginationFunc from "../../../../helper/hooks/pagination";
import FlexioPoolHeader from "../components/Header";
import FlexioPoolTable from "../components/FlexioPoolTable";
import FilterFlexioPool from "../components/Filter";
import PoolStatus from "components/items/PoolStatus";
import flexioPoolApi from "modules/client/pool/store/api";
import { POOL_PACKAGE_STATUSES } from "helper/status";
import { separateStatusUnderlineLetter } from "modules/shared/PickupDropoff/helper";
import { setSuccessMessage } from "app/global/slice";
import {
  selectMetadata,
  updatePaginationPackages
} from "modules/client/pool/store/slice";
import "modules/admin/orders/styles/routes.scss";
import { doExportCsv } from "helper/export";
import { refreshPackagesV2 } from "helper/getPackagesV2";
import { buildFirstLevelFilterPoolExport } from "helper/util";
import { saveAsDraftNewOrder } from "modules/client/active-orders/store/orderSlice";
import { generateId } from "helper/util";
import { useHistory } from "react-router-dom";
import { selectAllPackageTypes } from "modules/client/package-types/store/slice";
import moment from "moment";
import axios from "axios";
import { getPackageTypeList } from "modules/client/package-types/store/thunks";
import { replaceElementInArray } from "helper/util";
import { findIndex, trim } from "lodash";
import facturaApi from "../../facturas/store/api";

const breadCrumbs = [{ text: "POOL PACKAGES" }];

const { TabPane } = Tabs;

const handleTimeImport = (time) => {
  return moment(time, "HH:mm");
};

const poolPackageStatus = Object.keys(POOL_PACKAGE_STATUSES);

const PACKAGE_DATA = {
  "Total Micro Packages": "Micro",
  "Total Small Packages": "Small",
  "Total Medium Packages": "Medium",
  "Total Large Packages": "Large",
  "Total Pallet Packages": "Pallet",
  "Total Other Packages": "Custom",
  "Total Postal Packages": "Postal (sobre)"
};
const PACKAGE_TYPE_MAPPPING = {
  Micro: "Total Micro Packages",
  Small: "Total Small Packages",
  Medium: "Total Medium Packages",
  Large: "Total Large Packages",
  Pallet: "Total Pallet Packages",
  Custom: "Total Other Packages",
  "Postal (sobre)": "Total Postal Packages"
};

const MAP_CSV_DATA = {
  Id: "id",
  "Stop Type": "type",
  "Contact Name": "contact_name",
  "Contact Email": "email",
  Notes: "note",
  "Stop Name": "location_name",
  "Stop Address": "physical_address",
  "Map Address": "address", // Map address for geocoding
  "Stop Time Window - Start": "from_time",
  "Stop Time Window - End": "to_time",
  "Waiting/Service Time": "duration",
  "Internal Route ID": "internalRouteId",
  "Internal Order ID": "internalOrderId",
  "Internal Customer ID": "internalCustomerId",
  Salesperson: "salesPerson",
  COD: "cod"
};

const FlexioPoolList = () => {
  // Calculate default date range: first day of last month to last day of current month
  const getDefaultDateRange = () => {
    const startOfLastMonth = moment().subtract(1, "months").startOf("month");
    const endOfCurrentMonth = moment().endOf("month");
    return [startOfLastMonth, endOfCurrentMonth];
  };

  const [showFilter, setShowFilter] = useState(false);
  const dispatch = useDispatch();
  const metadata = useSelector(selectMetadata);
  const [axiosPackageList, setAxiosPackageList] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [filterValues, setFilterValues] = useState({
    pickupDateRange: getDefaultDateRange()
  });
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [batchStatusUpdate, setBatchStatusUpdate] = useState(false);
  const [packageArray, setPackageArray] = useState([]);
  const [selectAll, setSelectAll] = useState();
  const [newStatus, setNewStatus] = useState();
  const [selectedPackage, setSelectedPackage] = useState();
  const [submiting, setSubmiting] = useState(false);
  const [selectedPoolOrder, setSelectedPoolOrder] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showAddToFacturaModal, setShowAddToFacturaModal] = useState(false);
  const [unpaidFacturas, setUnpaidFacturas] = useState([]);
  const [selectedFacturaId, setSelectedFacturaId] = useState(null);
  const [selectedPackageForFactura, setSelectedPackageForFactura] = useState(null);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const packageTypeList = useSelector(selectAllPackageTypes);
  const history = useHistory();
  const pagination = useMemo(
    () =>
      paginationFunc(
        { ...metadata, itemsPerPage: metadata?.itemsPerPage || 20 },
        "Packages",
        updatePaginationPackages,
        dispatch
      ),
    [metadata, dispatch]
  );

  const myReduxState = useSelector((state) => state.myReducer);

  const page = useMemo(() => metadata?.currentPage || 1, [metadata?.currentPage]);
  const pageSize = useMemo(() => metadata?.itemsPerPage || 20, [metadata?.itemsPerPage]);

  const getPackages = useCallback(() => {
    refreshPackagesV2(
      filterValues,
      page,
      pageSize,
      setAxiosPackageList,
      updatePaginationPackages,
      dispatch
    );
  }, [filterValues, page, pageSize, dispatch]);

  useEffect(() => {
    getPackages();
  }, [getPackages]);

  useEffect(() => {
    dispatch(getPackageTypeList({ pageSize: 1000 }));
  }, [dispatch]);

  // method that handles apply button filter request
  const handleClickFilter = () => {
    getPackages();
  };
  // method that handles Clear button reset
  const handleReset = () => {
    setFilterValues({ pickupDateRange: getDefaultDateRange() });
  };

  const handleFilterChange = (newFilterValues) => {
    setFilterValues(newFilterValues);
  };

  const onExport = async () => {
    try {
      setExporting(true);
      await doExportCsv({
        path: "orderv2/export",
        name: `pool-orders-${new Date().getTime()}`,
        params: {
          filter: buildFirstLevelFilterPoolExport(
            filterValues?.pickupDateRange?.length > 0
              ? {
                  ...filterValues,
                  startPickupDate: filterValues.pickupDateRange[0],
                  endPickupDate: filterValues.pickupDateRange[1]
                }
              : {
                  ...filterValues
                }
          )
        }
      });
    } finally {
      setExporting(false);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      setSubmiting(true);

      await flexioPoolApi.updatePackageStatus(selectedPackage?.id, {
        status: newStatus
      });

      dispatch(setSuccessMessage("Updated status successfully!"));
    } catch (err) {
      console.error("Error in handlePackageUpdate:", err);
    } finally {
      setSubmiting(false);
      setNewStatus(null);
      setShowUpdateStatus(false);
      getPackages();
    }
  };

  /* Adds all the packages visible on the current page to the array for
  batch status update*/

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    const selectedPackages = selectAll ? [] : axiosPackageList?.map((pack) => pack.id);
    setPackageArray(selectedPackages);
  };

  /* Adds packages to the array for batch status update one by one by clicking the select
  checkbox on the modal table */

  const handlePackageArray = (pack) => {
    const tempArr = packageArray;
    /* Checks if the package already exists on the array to remove it if we uncheck the box */
    if (tempArr.includes(pack)) {
      const index = tempArr.indexOf(pack);
      tempArr.splice(index, 1);
      setPackageArray(tempArr);
    } else {
      packageArray.push(pack);
    }
  };

  /* Maps through the ids in the package array and performs the update on each one */

  const handleBatchUpdate = async () => {
    for (const packageId of packageArray) {
      try {
        setSubmiting(true);
        const updatedPkgV2Status = await flexioPoolApi.updatePackageStatus(packageId, {
          status: newStatus
        });
        setSelectedPoolOrder([updatedPkgV2Status]);
      } catch (err) {
        console.error("Error in handleBatchUpdate:", err);
      }
    }
    dispatch(setSuccessMessage("Updated status successfully!"));
    setSubmiting(false);
    setShowUpdateStatus(false);
    setBatchStatusUpdate(false);
    setPackageArray([]);
    setSelectAll(false);
    getPackages();
  };

  /* State for tab change */
  const handleTabChange = (activeKey) => {
    // Preserve the date range if it's been set, otherwise use default
    const currentDateRange = filterValues.pickupDateRange || getDefaultDateRange();

    if (activeKey === "1") {
      setShowFilter(false);
      setFilterValues({ pickupDateRange: currentDateRange });
    } else if (activeKey === "2") {
      setShowFilter(false);
      setFilterValues({ status: ["submitted"], pickupDateRange: currentDateRange });
    } else if (activeKey === "3") {
      setShowFilter(true);
      setFilterValues({ status: ["at_warehouse"], pickupDateRange: currentDateRange });
    } else {
      setShowFilter(false);
      setFilterValues({ pickupDateRange: currentDateRange });
    }
  };
  const handleChangePoolToShift = (keys, poolOrders) => {
    const currentPageRowKeys = axiosPackageList.map((item) => item.id);

    // Filter out deselected items from previous selection
    const updatedRowKeys = selectedRowKeys
      .filter((key) => !currentPageRowKeys.includes(key))
      .concat(keys);

    // Update selected row objects (merged with existing)
    const updatedSelectedRowsMap = new Map();

    // Add previously selected rows
    selectedPoolOrder.forEach((row) => {
      updatedSelectedRowsMap.set(row.id, row);
    });

    // Add newly selected rows
    poolOrders.forEach((row) => {
      updatedSelectedRowsMap.set(row.id, row);
    });

    // Filter out rows that are no longer selected
    const filteredSelectedRows = Array.from(updatedSelectedRowsMap.values()).filter(
      (row) => updatedRowKeys.includes(row.id)
    );

    setSelectedRowKeys(updatedRowKeys);
    setSelectedPoolOrder(filteredSelectedRows);
  };

  /*** HANDLE POOL TO SHIFT ***/

  const handlePoolToShift = async () => {
    console.log({ selectedPoolOrder, selectedRowKeys });

    //   /* Don't Allow Mix status */
    const isMultipleStatus = selectedPoolOrder.reduce((set, currPoolOrder) => {
      if (poolPackageStatus.includes(currPoolOrder.status)) {
        set.add(currPoolOrder.status);
      }
      return set;
    }, new Set());

    if (isMultipleStatus.size >= 2) {
      return message.info("There are different statuses in your order. Please use one.");
    }

    // quick loop checking each package status against first, AND EACH client id ()
    let allSameStatus = true;
    let firstStatus = "";
    let allSameClientId = true;
    let firstClientId = "";

    selectedPoolOrder.forEach((individualPackage, index) => {
      if (index === 0) {
        firstStatus = individualPackage.status;
        firstClientId = individualPackage.clientId;
      } else {
        if (individualPackage.status !== firstStatus) {
          allSameStatus = false;
        }
        if (individualPackage.clientId !== firstClientId) {
          allSameClientId = false;
        }
      }
    });

    // Determine targetClientId based on logic
    let targetClientId = "";

    if (firstStatus === "Dropoff" && allSameClientId) {
      targetClientId = firstClientId;
    } else {
      targetClientId =
        window.REACT_APP_DD_ENV !== "prod" ? "" : "5fb257dd-5f77-4398-9bc0-360d970d6c21";
    }

    /* package types */
    const pickups = [];
    const dropoffs = [];
    let draftPackages = [];
    let pickupId = 0;

    const packageCount = selectedPoolOrder.reduce((acc, pkg) => {
      acc[pkg.packageTypeId] = (acc[pkg.packageTypeId] || 0) + 1;
      return acc;
    }, {});

    //console.log("🦾 0.0 - before loop - packageCount: ", packageCount);
    //console.log("🦾 0.1 - before loop - full selectedPoolOrder: ", selectedPoolOrder);

    let pickupPackageTypesQty = {};
    /* 
      Manages validation for when we need to stop building pool order if there are
      any errors
    */
    let isCancelBuildPoolOrder = false;

    /* Main loop - build shiftRow array. for Each "poolPkg" is the individual package */
    const shiftRow = [];
    for (let poolIndex = 0; poolIndex < selectedPoolOrder.length; poolIndex++) {
      const poolPkg = selectedPoolOrder[poolIndex];

      //console.log("🦾 0.2 - foreach poolPkg in selectedPoolOrder (at top):");
      //console.log(poolPkg);
      let rowId = poolIndex + 1;

      const packageType = packageTypeList.find((p) => p.id === poolPkg.packageTypeId);
      const packageTypeName = packageType ? packageType.name : "Other";

      //console.log("🦾 0.3 - did we find type or other? packageTypeName: ", packageTypeName);

      /* PICKUP ROUTE - PICKUP TO WAREHOUSE */
      if (poolPkg?.status === "submitted") {
        if (Object.keys(packageCount).includes("null")) {
          isCancelBuildPoolOrder = true;
          message.error(`One of your packages does not have a valid Package Type.`, 5);
          break;
        }
        for (const pkgKey in packageCount) {
          //console.log("🦾 0.4 - foreach pkgKey in packageCount (at top):");
          //console.log(pkgKey);
          const name = packageTypeList.find((item) => item.id === pkgKey).name;
          pickupPackageTypesQty[name] = packageCount[pkgKey];

          //console.log("🦾 0.4.1 - name:");
          //console.log(name);
        }
        pickupId = rowId;
        if (!shiftRow.find((dropItem) => dropItem["Stop Type"] === "Dropoff")) {
          shiftRow.push({
            Id: selectedPoolOrder.length + 1,
            "Stop Type": "Dropoff",
            "Pickup Id": pickupId,
            "Contact Name": "Flexio Office",
            "Contact Phone": "+1787-123-4567",
            "Contact Email": "dispatch@goflexio.com",
            Notes: "Double check Pool package IDs",
            "Stop Name": "Flexio Office",
            "Stop Address": "CW72+8GR, San Juan, 00920",
            "Stop Time Window - Start": "9:00",
            "Stop Time Window - End": "16:00",
            "Waiting/Service Time": 10,
            "Internal Route ID": "",
            "Internal Order ID": "",
            "Internal Customer ID": "",
            Salesperson: "",
            COD: "",
            "Total Micro Packages": pickupPackageTypesQty["Micro"] || 0,
            "Total Small Packages": pickupPackageTypesQty["Small"] || 0,
            "Total Medium Packages": pickupPackageTypesQty["Medium"] || 0,
            "Total Large Packages": pickupPackageTypesQty["Large"] || 0,
            "Total Pallet Packages": pickupPackageTypesQty["Pallet"] || 0,
            "Total Other Packages": pickupPackageTypesQty["Other"] || 0,
            "Total Postal Packages": pickupPackageTypesQty["Postal (sobre)"] || 0,
            "Stop Latitude": 18.4124945,
            "Stop Longitude": -66.0964799,
            Barcode: poolPkg?.barcode,
            packagesV2: [poolPkg]
          });
        }

        // Determine the quantity property to update
        const quantityKey =
          PACKAGE_TYPE_MAPPPING[packageTypeName] || "Total Other Packages";

        //console.log("🦾 pickup? quantityKey: ", quantityKey);

        // Check if dropoff already exists
        const existingPickup = shiftRow.find(
          (pickupLine) =>
            pickupLine["Stop Address"] ===
            (poolPkg?.pickupDisplayAddress || poolPkg?.pickupAddress)
        );

        let pickupRow = {};

        if (existingPickup) {
          // find existing dropoff in array
          const pickupStopIndex = shiftRow
            .map((shiftPickup) => shiftPickup["Stop Address"])
            .indexOf(existingPickup["Stop Address"]);

          shiftRow[pickupStopIndex] = {
            ...shiftRow[pickupStopIndex],
            [quantityKey]: shiftRow[pickupStopIndex][quantityKey] + 1,
            packagesV2: [...shiftRow[pickupStopIndex].packagesV2, poolPkg]
          };
        } else {
          pickupRow = {
            Id: pickupId,
            "Stop Type": "Pickup",
            "Pickup Id": "-",
            "Contact Name": poolPkg?.pickupContactName,
            "Contact Phone": poolPkg?.pickupContactPhone,
            "Contact Email": poolPkg?.pickupContactEmail,
            Notes: poolPkg?.note || "",
            "Stop Name": poolPkg?.pickupStopName,
            "Stop Address": poolPkg?.pickupDisplayAddress || poolPkg?.pickupAddress,
            "Map Address": poolPkg?.pickupAddress,
            "Stop Latitude": poolPkg?.pickupLat,
            "Stop Longitude": poolPkg?.pickupLng,
            "Stop Time Window - Start": "8:00",
            "Stop Time Window - End": "17:00",
            "Waiting/Service Time": 5,
            "Internal Route ID": "",
            "Internal Order ID": poolPkg?.id || "",
            "Internal Customer ID": "",
            Salesperson: "",
            COD: "",
            "Total Micro Packages": 0,
            "Total Small Packages": 0,
            "Total Medium Packages": 0,
            "Total Large Packages": 0,
            "Total Pallet Packages": 0,
            "Total Other Packages": 0,
            "Total Postal Packages": 0,
            Barcode: poolPkg?.barcode,
            packagesV2: [poolPkg]
          };

          pickupRow[quantityKey] = 1;

          // add new dropoff to array
          shiftRow.push(pickupRow);
        }

        /* DROP OFF ROUTE - WAREHOUSE TO DROP OFF */
      } else {
        // get package types
        for (const pkgKey in packageCount) {
          const name = packageTypeList.find((item) => item.id === pkgKey).name;
          pickupPackageTypesQty[name] = packageCount[pkgKey];
        }

        // static define pickup id since all will be from warehouse
        pickupId = 1;

        // add the static pickup location - the if validation is extraneous
        if (!shiftRow.find((pickupItem) => pickupItem["Stop Type"] === "Pickup")) {
          shiftRow.push({
            Id: pickupId,
            "Stop Type": "Pickup",
            "Pickup Id": "-",
            "Contact Name": "Flexio",
            "Contact Phone": "+1787-123-4567",
            "Contact Email": "dispatch@goflexio.com",
            Notes: "Double check Pool package IDs",
            "Stop Name": "Flexio Office",
            "Stop Address": "CW72+8GR, San Juan, 00920",
            "Stop Time Window - Start": "9:00",
            "Stop Time Window - End": "16:00",
            "Waiting/Service Time": 10,
            "Internal Route ID": "",
            "Internal Order ID": "",
            "Internal Customer ID": "",
            Salesperson: "",
            COD: "",
            "Total Micro Packages": pickupPackageTypesQty["Micro"] || 0,
            "Total Small Packages": pickupPackageTypesQty["Small"] || 0,
            "Total Medium Packages": pickupPackageTypesQty["Medium"] || 0,
            "Total Large Packages": pickupPackageTypesQty["Large"] || 0,
            "Total Pallet Packages": pickupPackageTypesQty["Pallet"] || 0,
            "Total Other Packages": pickupPackageTypesQty["Other"] || 0,
            "Total Postal Packages": pickupPackageTypesQty["Postal (sobre)"] || 0,
            "Stop Latitude": 18.4124945,
            "Stop Longitude": -66.0964799,
            //Barcode: poolPkg?.barcode,
            packagesV2: selectedPoolOrder
          });
        }

        // Determine the quantity property to update
        const quantityKey =
          PACKAGE_TYPE_MAPPPING[packageTypeName] || "Total Other Packages";
        /* If dropoff address already exists in shiftRow array, find and update qty*/
        const existingDropoff = shiftRow.find(
          (dropoff) =>
            dropoff["Stop Type"] === "Dropoff" &&
            dropoff["Stop Address"] ===
              (poolPkg?.dropoffDisplayAddress || poolPkg?.dropoffAddress)
        );

        let dropoffRow = {};
        /*
          if (existingDropoff) {
            // Find existing dropoff in array
            const dropStopIndex = shiftRow.findIndex(
              (shiftDrop) => shiftDrop["Stop Type"] === "Dropoff" && shiftDrop["Stop Address"] === poolPkg?.dropoffAddress
            );

            // Update the existing dropoff by adding the current package to packagesV2
            shiftRow[dropStopIndex] = {
              ...shiftRow[dropStopIndex],
              packagesV2: [...shiftRow[dropStopIndex].packagesV2, poolPkg], // Add this package to existing array
              [quantityKey]: shiftRow[dropStopIndex][quantityKey] + 1
            };
          } else {
           */
        dropoffRow = {
          Id: rowId + pickupId,
          "Stop Type": "Dropoff",
          "Pickup Id": pickupId,
          "Contact Name": poolPkg?.dropoffContactName,
          "Contact Phone": poolPkg?.dropoffContactPhone,
          "Contact Email": poolPkg?.dropoffContactEmail,
          Notes: poolPkg?.note || "",
          "Stop Name": poolPkg?.dropoffStopName,
          "Stop Address": poolPkg?.dropoffDisplayAddress || poolPkg?.dropoffAddress,
          "Map Address": poolPkg?.dropoffAddress,
          "Stop Latitude": poolPkg?.dropoffLat,
          "Stop Longitude": poolPkg?.dropoffLng,
          "Stop Time Window - Start": "8:00",
          "Stop Time Window - End": "17:00",
          "Waiting/Service Time": 5,
          "Internal Route ID": "",
          "Internal Order ID": poolPkg?.id || "",
          "Internal Customer ID": "",
          "Total Micro Packages": 0,
          "Total Small Packages": 0,
          "Total Medium Packages": 0,
          "Total Large Packages": 0,
          "Total Pallet Packages": 0,
          "Total Other Packages": 0,
          "Total Postal Packages": 0,
          Salesperson: "",
          COD: "",
          Barcode: poolPkg?.barcode,
          packagesV2: [poolPkg],
          [quantityKey]: 1
        };
        //dropoffRow[quantityKey] = 1;

        // add new dropoff to array
        shiftRow.push(dropoffRow);
        //}
      }
    }

    // This will stop going to next page to create shift order
    if (isCancelBuildPoolOrder) {
      return;
    }

    // debugging for shiftRow import
    //console.log("🦾 shiftRow: ", shiftRow);

    /* AVL - get lat long for each place */
    let places = { places: {} };
    shiftRow.forEach(async (item) => {
      const displayAddr = item["Stop Address"];
      const mapAddr = item["Map Address"] || displayAddr; // fallback to display if no map address
      places.places[displayAddr] = { address: mapAddr }; // geocode using map address
      let latitude = Number(item["Stop Latitude"]);
      let longitude = Number(item["Stop Longitude"]);
      if (latitude && longitude) {
        places.places[displayAddr]["latitude"] = latitude;
        places.places[displayAddr]["longitude"] = longitude;
      }
    });

    const response = await axios.post(window.REACT_APP_GEOCODE_API_URI, places);

    /* map the data array to CSV for import */
    for (let i = 0; i < shiftRow.length; i++) {
      const poolShiftItem = shiftRow[i];
      const packageName = poolShiftItem["Item Name"];
      const id = poolShiftItem["Id"];
      let location = {};
      const keys = Object.keys(poolShiftItem);

      keys.forEach((key) => {
        if (MAP_CSV_DATA[key]) {
          location = {
            ...location,
            [MAP_CSV_DATA[key]]: poolShiftItem[key]
          };
        }
      });

      let phonNumbers = [
        poolShiftItem["Contact Phone"],
        poolShiftItem["Contact Phone 1"],
        poolShiftItem["Contact Phone 2"]
      ].filter((n) => n);
      const phone_numbers = phonNumbers.map((phone) => {
        let trimmedPhone = trim(phone);
        if (trimmedPhone.match(/^(787)|^(939)/)) {
          trimmedPhone = "+1" + trimmedPhone;
        }
        return {
          type: "contact",
          phone: (trimmedPhone?.indexOf("+") === -1 ? "+" : "") + trimmedPhone
        };
      });

      const displayAddr = poolShiftItem["Stop Address"]; // Physical address for display
      const mapAddr = poolShiftItem["Map Address"]; // Map address for geocoding
      const originalLat = Number(poolShiftItem["Stop Latitude"]);
      const originalLng = Number(poolShiftItem["Stop Longitude"]);

      // Use original coordinates if available, otherwise fallback to geocoding
      let latitude = originalLat;
      let longitude = originalLng;

      if (!latitude || !longitude) {
        const place_response = response?.data?.places?.[displayAddr];
        latitude = place_response?.latitude || 0;
        longitude = place_response?.longitude || 0;
      }

      let address = {
        address: mapAddr || displayAddr, // Use map address for coordinates, fallback to display
        lngLat: [longitude || 0, latitude || 0]
      };

      if (firstStatus === "submitted") {
        // status submitted
        if (poolShiftItem["Stop Type"].toLowerCase() === "pickup") {
          Object.keys(PACKAGE_DATA).forEach((packageKey) => {
            if (poolShiftItem[packageKey]) {
              const packageId = generateId("PickupPack");
              const genericName = PACKAGE_DATA[packageKey];
              const packageType = packageTypeList.find(
                (pack) => pack.name === genericName
              );

              draftPackages.push({
                id: packageId,
                name: packageName || genericName,
                action: null,
                quantity: poolShiftItem[packageKey],
                packageTypeId: packageType.id,
                maxLength: packageType.maxLength,
                maxWeight: packageType.maxWeight,
                maxVolume: packageType.maxVolume,
                pickupId: id,
                barcode: poolShiftItem.Barcode,

                // Filter packagesV2 to only include the item with matching barcode
                // if you don't do this, you get ghost extra packages
                packagesV2: poolShiftItem?.packagesV2
                  .filter((pkgV2) => pkgV2.barcode === poolShiftItem.Barcode)
                  .map((pkgV2) => ({
                    ...pkgV2,
                    packageTypeName: packageType?.name
                  }))
              });
            }
          });

          const pickMaxPosition = Math.max(...pickups.map((p) => p.position), 0);
          if (findIndex(pickups, { id }) !== -1) continue;
          pickups.push({
            ...location,
            id,
            address,
            from_time: handleTimeImport(poolShiftItem["Stop Time Window - Start"]),
            to_time: handleTimeImport(poolShiftItem["Stop Time Window - End"]),
            phone_numbers,
            lat: latitude,
            lng: longitude,
            position: pickMaxPosition + 1
          });
        } else {
          Object.keys(PACKAGE_DATA).forEach((packageKey) => {
            //FIXME: calculate total quantity late
            const packagePickup = draftPackages.find(
              (pack) =>
                pack.pickupId === poolShiftItem["Pickup Id"] &&
                pack.name === (packageName || PACKAGE_DATA[packageKey])
            );

            if (poolShiftItem[packageKey]) {
              if (!packagePickup) return;
              let updatedDropoffs = packagePickup?.dropoffs;
              const existingDropoffIndex = updatedDropoffs.findIndex((d) => d.id === id);

              if (existingDropoffIndex !== -1) {
                // If the dropoff exists, update the quantity
                updatedDropoffs[existingDropoffIndex] = {
                  ...updatedDropoffs[existingDropoffIndex],
                  quantity:
                    (updatedDropoffs[existingDropoffIndex].quantity || 0) +
                    poolShiftItem[packageKey]
                };
              } else {
                // If it doesn't exist, add a new dropoff
                updatedDropoffs.push({
                  id,
                  quantity: poolShiftItem[packageKey]
                });
              }

              const packageWithDropoff = {
                ...packagePickup,
                dropoffs: updatedDropoffs
              };

              draftPackages = replaceElementInArray(draftPackages, packageWithDropoff);
            }
          });
          const dropMaxPosition = Math.max(...dropoffs.map((p) => p.position), 0);
          if (findIndex(dropoffs, { id }) !== -1) continue;
          dropoffs.push({
            ...location,
            id,
            address,
            from_time: handleTimeImport(poolShiftItem["Stop Time Window - Start"]),
            to_time: handleTimeImport(poolShiftItem["Stop Time Window - End"]),
            phone_numbers,
            lat: latitude,
            lng: longitude,
            position: dropMaxPosition + 1
          });
        }
        // P2S - WAREHOUSE TO DROPOFF ROUTES
      } else {
        //
        if (poolShiftItem["Stop Type"].toLowerCase() === "pickup") {
          /*
            Object.keys(PACKAGE_DATA).forEach((packageKey) => {
              if (poolShiftItem[packageKey]) {
                const packageId = generateId("PickupPack");
                const genericName = PACKAGE_DATA[packageKey];
                const packageType = packageTypeList.find(
                  (pack) => pack.name === genericName
                );

                draftPackages.push({
                  id: packageId,
                  name: packageName || genericName,
                  action: null,
                  quantity: poolShiftItem[packageKey],
                  packageTypeId: packageType.id,
                  maxLength: packageType.maxLength,
                  maxWeight: packageType.maxWeight,
                  maxVolume: packageType.maxVolume,
                  pickupId: id,
                  barcode: poolShiftItem.Barcode,

                  // Filter packagesV2 to only include the item with matching barcode
                  // if you don't do this, you get ghost extra packages
                  packagesV2: poolShiftItem?.packagesV2
                    .filter(pkgV2 => pkgV2.barcode === poolShiftItem.Barcode)
                    .map((pkgV2) => ({
                      ...pkgV2,
                      packageTypeName: packageType?.name
                    }))

                });
              }
            });
            */

          const pickMaxPosition = Math.max(...pickups.map((p) => p.position), 0);
          if (findIndex(pickups, { id }) !== -1) continue;
          pickups.push({
            ...location,
            id,
            address,
            from_time: handleTimeImport(poolShiftItem["Stop Time Window - Start"]),
            to_time: handleTimeImport(poolShiftItem["Stop Time Window - End"]),
            phone_numbers,
            lat: latitude,
            lng: longitude,
            position: pickMaxPosition + 1
          });

          // else dropoff stops on P2S dropoff route - here is where we add packages
        } else {
          /*
            Object.keys(PACKAGE_DATA).forEach((packageKey) => {
              //FIXME: calculate total quantity late
              const packagePickup = draftPackages.find(
                (pack) =>
                  pack.pickupId === poolShiftItem["Pickup Id"] &&
                  pack.name === (packageName || PACKAGE_DATA[packageKey])
              );

              if (poolShiftItem[packageKey]) {
                const packageWithDropoff = {
                  ...packagePickup,
                  dropoffs:
                    packagePickup?.dropoffs?.length > 0
                      ? [
                          ...packagePickup?.dropoffs,
                          {
                            id,
                            quantity: poolShiftItem[packageKey]
                          }
                        ]
                      : [
                          {
                            id,
                            quantity: poolShiftItem[packageKey]
                          }
                        ]
                };
                draftPackages = replaceElementInArray(draftPackages, packageWithDropoff);
              }
            });
            */

          Object.keys(PACKAGE_DATA).forEach((packageKey) => {
            if (poolShiftItem[packageKey]) {
              const packageId = generateId("PickupPack");
              const genericName = PACKAGE_DATA[packageKey];
              const packageType = packageTypeList.find(
                (pack) => pack.name === genericName
              );

              draftPackages.push({
                id: packageId,
                name: packageName || genericName,
                action: null,
                quantity: poolShiftItem[packageKey],
                packageTypeId: packageType.id,
                maxLength: packageType.maxLength,
                maxWeight: packageType.maxWeight,
                maxVolume: packageType.maxVolume,
                //pickupId: id,
                pickupId: 1,
                dropoffs: [
                  {
                    id,
                    quantity: poolShiftItem[packageKey]
                  }
                ],
                barcode: poolShiftItem.Barcode,

                // Filter packagesV2 to only include the item with matching barcode
                // if you don't do this, you get ghost extra packages
                packagesV2: poolShiftItem?.packagesV2
                  .filter((pkgV2) => pkgV2.barcode === poolShiftItem.Barcode)
                  .map((pkgV2) => ({
                    ...pkgV2,
                    packageTypeName: packageType?.name
                  }))
              });
            }
          });

          if (findIndex(dropoffs, { id }) !== -1) continue;

          const dropMaxPosition = Math.max(...dropoffs.map((p) => p.position), 0);

          dropoffs.push({
            ...location,
            id,
            address,
            from_time: handleTimeImport(poolShiftItem["Stop Time Window - Start"]),
            to_time: handleTimeImport(poolShiftItem["Stop Time Window - End"]),
            phone_numbers,
            lat: latitude,
            lng: longitude,
            position: dropMaxPosition + 1
          });
        }
      }
    }

    /* Multiple Pickup One dropoff has a problem with how we pair stops.
      We MUST use isAllPickup for this model; so set that for the Pickup > Warehouse route
      based on all status == submitted */

    // this variable will be true only if all packages are "submitted"
    const forDispatchIsAllSame = selectedPoolOrder.every(
      (individualPackage) => individualPackage.status === "submitted"
    );

    // Final debugging - this helped to see an issue with draft packages
    //console.log("🦾 finale");
    //console.log("🦾 pickups: ", pickups);
    //console.log("🦾 dropoffs: ", dropoffs);
    //console.log("🦾 draftPackages: ", draftPackages);

    dispatch(
      saveAsDraftNewOrder({
        data: {
          poolStatus: firstStatus,
          clientId: targetClientId,
          isPoolToShift: true,
          pickups,
          dropoffs,
          packages: draftPackages,
          isAllPackage: forDispatchIsAllSame // see above
        }
      })
    );
    history.push("/admin/jobs/new?action=new");
  };

  const handleCreateFactura = async (poolPackage) => {
    try {
      // Extract client ID from the pool package
      const clientId = poolPackage.orderV2?.client?.id || poolPackage.clientId;

      if (!clientId) {
        message.error("Cannot create factura: Package has no associated client");
        return;
      }

      // Create new factura with this pool package
      const newFactura = await facturaApi.createFacturaWithPoolPackages(clientId, [
        poolPackage.id
      ]);

      message.success("Factura created successfully!");

      // Navigate to the new factura edit page - don't call getPackages after navigation
      history.push(`/admin/facturas/edit/${newFactura.id}`);
    } catch (error) {
      console.error("Error creating factura:", error);
      message.error("Failed to create factura");
    }
  };

  // Function to get client ID for a pool package
  const getClientIdForPackage = (poolPackage) => {
    return poolPackage.orderV2?.client?.id || poolPackage.clientId;
  };

  const handleShowAddToFactura = async (poolPackage) => {
    if (!poolPackage?.id) {
      message.error("Package ID not available");
      return;
    }

    setLoadingFacturas(true);
    try {
      // Get client ID from the pool package
      const clientId = getClientIdForPackage(poolPackage);
      if (!clientId) {
        message.error("Unable to determine client for this package");
        setLoadingFacturas(false);
        return;
      }

      const response = await facturaApi.getUnpaidFacturas(clientId);
      const facturas = response.data || [];

      if (facturas.length === 0) {
        message.info(
          "No unpaid facturas found for this client. Create a new factura instead."
        );
        return;
      }

      setUnpaidFacturas(facturas);
      setSelectedPackageForFactura(poolPackage);
      setShowAddToFacturaModal(true);
    } catch (error) {
      console.error("Load facturas error:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Unknown error";
      message.error("Failed to load unpaid facturas: " + errorMessage);
    } finally {
      setLoadingFacturas(false);
    }
  };

  const handleAddToExistingFactura = async () => {
    if (!selectedFacturaId) {
      message.error("Please select a factura");
      return;
    }

    if (!selectedPackageForFactura) {
      message.error("No package selected");
      return;
    }

    setLoadingFacturas(true);
    try {
      await facturaApi.addPoolPackagesToFactura(selectedFacturaId, [
        selectedPackageForFactura.id
      ]);
      message.success("Package added to factura successfully!");

      setShowAddToFacturaModal(false);
      setSelectedFacturaId(null);
      setSelectedPackageForFactura(null);

      // Navigate to the updated factura - don't call getPackages after navigation
      history.push(`/admin/facturas/edit/${selectedFacturaId}`);
    } catch (error) {
      console.error("Add to factura error:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Unknown error";
      message.error("Failed to add package to factura: " + errorMessage);
    } finally {
      setLoadingFacturas(false);
    }
  };

  const handleModalCancel = () => {
    setShowAddToFacturaModal(false);
    setSelectedFacturaId(null);
    setSelectedPackageForFactura(null);
  };

  const handleAddToFactura = handleShowAddToFactura;

  return (
    <Tabs defaultActiveKey="1" type="card" onChange={handleTabChange}>
      <TabPane tab="All" key="1">
        <FlexioPoolHeader
          breadCrumbs={breadCrumbs}
          showExport={true}
          setShowFilter={setShowFilter}
          onExport={onExport}
          exporting={exporting}
          setBatchStatusUpdate={setBatchStatusUpdate}
          setShowUpdateStatus={setShowUpdateStatus}
          handlePoolToShift={() => {
            message.destroy();
            handlePoolToShift();
          }}
          canCreateShiftOrder={true}
        />
        {showFilter && (
          <FilterFlexioPool
            setShowFilter={setShowFilter}
            onFilterChange={handleFilterChange}
            defaultValues={filterValues}
            handleClickFilter={handleClickFilter}
            onReset={handleReset}
          />
        )}
        <div className="admin-content">
          <FlexioPoolTable
            dataSource={axiosPackageList}
            pagination={pagination}
            setSelectedPackage={setSelectedPackage}
            setShowUpdateStatus={setShowUpdateStatus}
            hiddenColumns={["Select"]}
            onCreateFactura={handleCreateFactura}
            onAddToFactura={handleAddToFactura}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys,
              onChange: (rowKeys, selectedRows) => {
                handleChangePoolToShift(rowKeys, selectedRows);
              }
            }}
          />
          {/* Added modal for the manual status update feature*/}
          {showUpdateStatus && (
            <Modal
              closable={false}
              open={true}
              title="Edit Package Status"
              width={"70%"}
              footer={
                <div className="buttons-modal">
                  <Button
                    size="large"
                    onClick={() => {
                      setShowUpdateStatus(false);
                      setNewStatus(null);
                      setSelectedPackage(null);
                      setPackageArray([]);
                      setSelectAll([]);
                    }}
                    loading={submiting}
                    disabled={submiting}
                  >
                    Cancel
                  </Button>

                  <Button
                    disabled={!newStatus || newStatus === selectedPackage?.status}
                    loading={submiting}
                    size="large"
                    type="primary"
                    onClick={!batchStatusUpdate ? handleUpdateStatus : handleBatchUpdate}
                  >
                    Update
                    <CheckCircleOutlined />
                  </Button>
                </div>
              }
            >
              <div className="d-flex flex-column gap-3 change-job-status-modal">
                {!batchStatusUpdate ? (
                  <Row className="gap-5">
                    <label>Current Status</label>
                    <div className="status">
                      <PoolStatus value={selectedPackage.status} />
                    </div>
                  </Row>
                ) : (
                  <>
                    <Row className="gap-5">
                      <label>Select Jobs</label>
                    </Row>
                    <Row className="gap-5">
                      <Checkbox onChange={handleSelectAll}>Select All</Checkbox>
                    </Row>
                    <div className="right-block jobs">
                      <FlexioPoolTable
                        dataSource={axiosPackageList}
                        pagination={pagination}
                        hiddenColumns={["Pickup Date", "Created At"]}
                        selectAll={selectAll}
                        handlePackageArray={handlePackageArray}
                      />
                    </div>
                  </>
                )}
                <Row className="flex-column gap-2">
                  <label>New Status</label>
                  <Select
                    listHeight={350}
                    size="large"
                    placeholder="Select status"
                    onChange={(status) => setNewStatus(status)}
                    style={{ width: "100%" }}
                    value={newStatus}
                  >
                    {_keys(POOL_PACKAGE_STATUSES).map((status) => (
                      <Select.Option key={status} value={status}>
                        {separateStatusUnderlineLetter(status)}
                      </Select.Option>
                    ))}
                  </Select>
                </Row>
              </div>
            </Modal>
          )}
        </div>
      </TabPane>
      <TabPane tab="Submitted" key="2">
        <FlexioPoolHeader
          breadCrumbs={breadCrumbs}
          showExport={true}
          setShowFilter={setShowFilter}
          onExport={onExport}
          exporting={exporting}
          setBatchStatusUpdate={setBatchStatusUpdate}
          setShowUpdateStatus={setShowUpdateStatus}
          handlePoolToShift={handlePoolToShift}
          canCreateShiftOrder={true}
        />
        {showFilter && (
          <FilterFlexioPool
            setShowFilter={setShowFilter}
            onFilterChange={handleFilterChange}
            defaultValues={filterValues}
            handleClickFilter={handleClickFilter}
            onReset={handleReset}
          />
        )}
        <div className="admin-content">
          <FlexioPoolTable
            dataSource={axiosPackageList}
            pagination={pagination}
            setSelectedPackage={setSelectedPackage}
            setShowUpdateStatus={setShowUpdateStatus}
            hiddenColumns={["Select"]}
            onCreateFactura={handleCreateFactura}
            onAddToFactura={handleAddToFactura}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys,
              onChange: (rowKeys, selectedRows) => {
                console.log({ selectedRows, rowKeys });
                handleChangePoolToShift(rowKeys, selectedRows);
              }
            }}
          />
          {/* Added modal for the manual status update feature*/}
          {showUpdateStatus && (
            <Modal
              closable={false}
              open={true}
              title="Edit Package Status"
              width={"70%"}
              footer={
                <div className="buttons-modal">
                  <Button
                    size="large"
                    onClick={() => {
                      setShowUpdateStatus(false);
                      setNewStatus(null);
                      setSelectedPackage(null);
                      setPackageArray([]);
                      setSelectAll([]);
                    }}
                    loading={submiting}
                    disabled={submiting}
                  >
                    Cancel
                  </Button>

                  <Button
                    disabled={!newStatus || newStatus === selectedPackage?.status}
                    loading={submiting}
                    size="large"
                    type="primary"
                    onClick={!batchStatusUpdate ? handleUpdateStatus : handleBatchUpdate}
                  >
                    Update
                    <CheckCircleOutlined />
                  </Button>
                </div>
              }
            >
              <div className="d-flex flex-column gap-3 change-job-status-modal">
                {!batchStatusUpdate ? (
                  <Row className="gap-5">
                    <label>Current Status</label>
                    <div className="status">
                      <PoolStatus value={selectedPackage.status} />
                    </div>
                  </Row>
                ) : (
                  <>
                    <Row className="gap-5">
                      <label>Select Jobs</label>
                    </Row>
                    <Row className="gap-5">
                      <Checkbox onChange={handleSelectAll}>Select All</Checkbox>
                    </Row>
                    <div className="right-block jobs">
                      <FlexioPoolTable
                        dataSource={axiosPackageList}
                        pagination={pagination}
                        hiddenColumns={["Pickup Date", "Created At", "Last Updated At"]}
                        selectAll={selectAll}
                        handlePackageArray={handlePackageArray}
                      />
                    </div>
                  </>
                )}
                <Row className="flex-column gap-2">
                  <label>New Status</label>
                  <Select
                    listHeight={350}
                    size="large"
                    placeholder="Select status"
                    onChange={(status) => setNewStatus(status)}
                    style={{ width: "100%" }}
                    value={newStatus}
                  >
                    {_keys(POOL_PACKAGE_STATUSES).map((status) => (
                      <Select.Option key={status} value={status}>
                        {separateStatusUnderlineLetter(status)}
                      </Select.Option>
                    ))}
                  </Select>
                </Row>
              </div>
            </Modal>
          )}
        </div>
      </TabPane>
      <TabPane tab="At Warehouse" key="3">
        <FlexioPoolHeader
          breadCrumbs={breadCrumbs}
          showExport={true}
          setShowFilter={setShowFilter}
          onExport={onExport}
          exporting={exporting}
          setBatchStatusUpdate={setBatchStatusUpdate}
          setShowUpdateStatus={setShowUpdateStatus}
          handlePoolToShift={handlePoolToShift}
          canCreateShiftOrder={true}
        />
        {showFilter && (
          <FilterFlexioPool
            setShowFilter={setShowFilter}
            onFilterChange={handleFilterChange}
            defaultValues={filterValues}
            handleClickFilter={handleClickFilter}
            onReset={handleReset}
          />
        )}
        <div className="admin-content">
          <FlexioPoolTable
            dataSource={axiosPackageList}
            pagination={pagination}
            setSelectedPackage={setSelectedPackage}
            setShowUpdateStatus={setShowUpdateStatus}
            hiddenColumns={["Select"]}
            onCreateFactura={handleCreateFactura}
            onAddToFactura={handleAddToFactura}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys,
              onChange: (rowKeys, selectedRows) => {
                handleChangePoolToShift(rowKeys, selectedRows);
              }
            }}
          />
          {/* Added modal for the manual status update feature*/}
          {showUpdateStatus && (
            <Modal
              closable={false}
              open={true}
              title="Edit Package Status"
              width={"70%"}
              footer={
                <div className="buttons-modal">
                  <Button
                    size="large"
                    onClick={() => {
                      setShowUpdateStatus(false);
                      setNewStatus(null);
                      setSelectedPackage(null);
                      setPackageArray([]);
                      setSelectAll([]);
                    }}
                    loading={submiting}
                    disabled={submiting}
                  >
                    Cancel
                  </Button>

                  <Button
                    disabled={!newStatus || newStatus === selectedPackage?.status}
                    loading={submiting}
                    size="large"
                    type="primary"
                    onClick={!batchStatusUpdate ? handleUpdateStatus : handleBatchUpdate}
                  >
                    Update
                    <CheckCircleOutlined />
                  </Button>
                </div>
              }
            >
              <div className="d-flex flex-column gap-3 change-job-status-modal">
                {!batchStatusUpdate ? (
                  <Row className="gap-5">
                    <label>Current Status</label>
                    <div className="status">
                      <PoolStatus value={selectedPackage.status} />
                    </div>
                  </Row>
                ) : (
                  <>
                    <Row className="gap-5">
                      <label>Select Jobs</label>
                    </Row>
                    <Row className="gap-5">
                      <Checkbox onChange={handleSelectAll}>Select All</Checkbox>
                    </Row>
                    <div className="right-block jobs">
                      <FlexioPoolTable
                        dataSource={axiosPackageList}
                        pagination={pagination}
                        hiddenColumns={["Pickup Date", "Created At", "Last Updated At"]}
                        selectAll={selectAll}
                        handlePackageArray={handlePackageArray}
                      />
                    </div>
                  </>
                )}
                <Row className="flex-column gap-2">
                  <label>New Status</label>
                  <Select
                    listHeight={350}
                    size="large"
                    placeholder="Select status"
                    onChange={(status) => setNewStatus(status)}
                    style={{ width: "100%" }}
                    value={newStatus}
                  >
                    {_keys(POOL_PACKAGE_STATUSES).map((status) => (
                      <Select.Option key={status} value={status}>
                        {separateStatusUnderlineLetter(status)}
                      </Select.Option>
                    ))}
                  </Select>
                </Row>
              </div>
            </Modal>
          )}
        </div>
      </TabPane>

      {/* Add to Factura Modal */}
      <Modal
        title="Add Pool Package to Existing Factura"
        open={showAddToFacturaModal}
        onOk={handleAddToExistingFactura}
        onCancel={handleModalCancel}
        confirmLoading={loadingFacturas}
        okText="Add to Factura"
        okButtonProps={{ disabled: !selectedFacturaId }}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Select an unpaid factura to add this pool package to:</p>
          {selectedPackageForFactura && (
            <div
              style={{
                padding: 12,
                background: "#f5f5f5",
                borderRadius: 4,
                marginBottom: 16,
                fontSize: 12
              }}
            >
              <strong>Package:</strong>{" "}
              {selectedPackageForFactura.id?.substring(0, 8).toUpperCase()}
              <br />
              <strong>Status:</strong> {selectedPackageForFactura.status}
              <br />
              <strong>Client:</strong>{" "}
              {selectedPackageForFactura.orderV2?.client?.name || "Unknown"}
            </div>
          )}
        </div>
        <Select
          placeholder="Select a factura"
          style={{ width: "100%" }}
          value={selectedFacturaId}
          onChange={setSelectedFacturaId}
          showSearch
          optionFilterProp="children"
        >
          {unpaidFacturas.map((factura) => (
            <Select.Option key={factura.id} value={factura.id}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <span style={{ fontFamily: "monospace", fontSize: "12px" }}>
                  {factura.facturaId}
                </span>
                <span style={{ color: "#666", fontSize: "12px" }}>
                  ${(Number(factura.total) || 0).toFixed(2)}
                </span>
              </div>
            </Select.Option>
          ))}
        </Select>
        {unpaidFacturas.length === 0 && (
          <div style={{ textAlign: "center", color: "#999", marginTop: 16 }}>
            No unpaid facturas available
          </div>
        )}
      </Modal>
    </Tabs>
  );
};
export default FlexioPoolList;
